// Shared "Incidents" component — the priority-tiered incident feed used by BOTH
// the dashboard SPA and the embeddable widget. Presentational only: each host
// supplies the data (its incidents hook) + nav callbacks, and loads the matching
// styles (`@netwatchai/chat-core/incidents.css`; the widget inlines it into its
// shadow DOM). Same shared-component pattern as `Outlook`.

import { useCallback, useEffect, useRef, useState } from 'react'
import { EmptyState } from './EmptyState'
import { FindingsBlock, type FindingsData } from './FindingsBlock'
import { isNewIncident, NEW_WINDOW_MINUTES } from './incidentRecency'
import { loadSeen, markSeen, seedSeen, subscribeSeen, type SeenState } from './incidentsSeen'
import {
  latestActivityIso, loadSort, saveSort, sortIncidents,
  SORT_LABELS, SORT_TITLES, type IncidentSort,
} from './incidentsSort'
import {
  DEFAULT_PERIOD, loadPeriod, PERIOD_GROUPS, PERIOD_LABELS, resolvePeriod, savePeriod,
  type PeriodKey,
} from './incidentsPeriod'
import type { CaseReport, Correlation } from './Outlook'
import { SEVERITY } from './severity'
import { SeverityIcon } from './Severity'

// --- canonical wire shapes (snake_case, mirror app/api/incidents.py) ---------

export type IncidentTier = 'needs_now' | 'worth_a_look' | 'routed_to_log' | 'resolved'
/** Investigation outcome, still carried on the wire. No longer a card badge —
 *  the pill slot now marks NEW incidents (docs/incident-new-badge.md); the
 *  verdict itself lives inside the expanded case report, where the evidence
 *  backing it is one line away. */
export type IncidentCause = 'found' | 'unclear' | 'pending' | 'noise'

/** One leg of a flapping card's up/down strip. `until` is null on the leg still
 *  running — draw it to "now". */
export interface FlapSegment {
  state: 'down' | 'up'
  at: string
  until?: string | null
  seconds: number
}

/** A flapping episode: the same trigger opening and clearing on repeat, held as
 *  ONE incident (docs/flapping.md). `held` is what the card's note promises —
 *  no new incident per flip, and no email until it settles. */
export interface FlapInfo {
  state: 'flapping' | 'settled'
  flips: number
  started_at?: string | null
  last_flip_at?: string | null
  settled_at?: string | null
  escalated_at?: string | null
  held: boolean
  window_minutes: number
  currently_down: boolean
  segments: FlapSegment[]
}

export interface Incident {
  id: string
  host?: string | null
  severity: number
  alert_name: string
  status: string
  strategy?: string | null
  /** AI-assigned filter tag (null until the investigation lands). */
  tag?: string | null
  opened_at?: string | null
  created_at?: string | null
  resolved_at?: string | null
  /** Feed cards group hosts sharing a trigger; the card wears the
   *  representative's opened_at, which can be days older (or newer) than
   *  other members. These carry the group's full opened_at span so the
   *  Today/Overdue chips can match on member dates, not the rep's. Absent on
   *  a pre-upgrade backend — the chips then fall back to opened_at. */
  newest_opened_at?: string | null
  oldest_opened_at?: string | null
  /** …and the group's resolved_at span — the Resolved tab sorts its cards on
   *  the newest, and the card's meta line reads "cleared X ago" off it.
   *  Absent on Live cards and pre-upgrade backends. */
  newest_resolved_at?: string | null
  oldest_resolved_at?: string | null
  member_count: number
  /** Distinct hosts in the group. Since instance-skeleton grouping, members
   *  can share one host (per-database fires), so member_count no longer
   *  implies a host count. Absent on a pre-upgrade backend — fall back to
   *  member_count, the old (cross-host-only) equation. */
  hosts_affected?: number
  correlation_reason?: string | null
  effective_importance: number
  cause: IncidentCause
  tier: IncidentTier
  /** Set only while a trigger is flapping (or has just settled) — the pill, the
   *  flip count and the strip. Absent on a pre-upgrade backend. */
  flap?: FlapInfo | null
}

export interface IncidentFeedSummary {
  needs_now: number
  active: number
  events_bundled: number
  routed_to_log: number
  resolved: number
  /** severity (0..5, JSON string keys) → open problems at that raw severity.
   *  One open incident = one active problem, so these mirror the monitoring
   *  dashboard's problems-by-severity numbers. Absent on a pre-upgrade
   *  backend — the banner then falls back to the tier counts. */
  problems_by_severity?: Record<string, number> | null
}

export interface IncidentFeed {
  items: Incident[]
  summary?: IncidentFeedSummary | null
}

/** One section of the R5 evidence trail (`incidents.evidence`) — the wire
 *  shape, kept typed for hosts that read the trail. Since amendment 20 the
 *  detail view no longer renders these directly: the kind-tagged sections
 *  are lifted into the findings block (`recurring`/`processes`) at
 *  investigation time. */
export interface IncidentEvidenceSection {
  title: string
  lines: string[]
  kind?: string | null
}

export interface IncidentDetailData extends Incident {
  trigger_id?: string | null
  case_report?: CaseReport | null
  correlation?: Correlation | null
  /** The code-built findings card (docs/findings-block-producers.md) — the
   *  evidence the case report is a reading of. Hosts load
   *  `@netwatchai/chat-core/findings.css`. Null before the block existed or
   *  for a client it is not enabled for. */
  findings?: FindingsData | null
  /** R5 typed trail: `{ evidence: { sections, checks, … }, problem_context }`.
   *  Only the kind-tagged evidence sections are rendered; null on legacy
   *  (pre-R5) investigations. */
  evidence?: { evidence?: { sections?: IncidentEvidenceSection[] } | null } | null
  /** R6 group inheritance: when set, case_report/correlation belong to the
   *  parent incident (this id) — the member wasn't investigated on its own.
   *  group_size is the parent's member count, for the banner. */
  report_inherited_from?: string | null
  group_size?: number | null
  user_importance?: number | null
  response_rating?: number | null
  user_feedback?: string | null
}

export interface IncidentFeedback {
  user_importance?: number
  response_rating?: number
  user_feedback?: string
}

/** One page of RESOLVED incidents (the History tab). */
export interface IncidentHistoryPage {
  items: Incident[]
  total: number
  page: number
  page_size: number
  /** tag → count over the whole resolved set (not this page, and not narrowed
   *  by the active filters) — the tag bubbles' counts. Absent on a pre-upgrade
   *  backend, which hides the tag filter on this tab. */
  tag_counts?: Record<string, number> | null
  /** The selected period held more resolved rows than the server's working-set
   *  cap — the cards cover the newest events only. Absent on a pre-upgrade
   *  backend. */
  truncated?: boolean
  /** Resolved rows behind the cards (the whole filtered set, capped) — lets
   *  the header read "N groups · M events". */
  events_total?: number | null
}

/** Resolved-tab filters — the Live toolbar's, applied SERVER-side because the
 *  tab is paginated (filtering the page in hand would hide matches on page 5). */
export interface IncidentHistoryFilters {
  /** Substring search over alert name + host. */
  q?: string
  /** A tag from `tag_counts`; `untagged` = not tagged yet. */
  tag?: string
  /** `today` = resolved since `dayStart`; `aged` = stayed open ≥24h. */
  window?: 'today' | 'aged'
  /** Browser-local midnight (ISO) that defines "today" — the same day boundary
   *  the Live chips use, so the two tabs agree. */
  dayStart?: string
  /** Period bounds from resolvePeriod(): only incidents resolved in
   *  [from, to) — `from` inclusive, `to` exclusive, either side open. */
  from?: string
  to?: string
}


export interface IncidentsProps {
  feed: IncidentFeed | null
  loading?: boolean
  error?: string | null
  /** Bundled alerts for a grouped incident (affected hosts + timeline).
   *  Live cards call it bare; Resolved-tab cards pass `resolved: true` plus
   *  their period AND toolbar filters (q/tag/aged) — the card's count comes
   *  from the filtered row set, so the member list must read the same set. */
  getMembers: (
    id: string,
    opts?: { resolved?: boolean; from?: string; to?: string; q?: string; tag?: string; aged?: boolean },
  ) => Promise<Incident[]>
  /** Full case_report + correlation for "Dig deeper". */
  getDetail: (id: string) => Promise<IncidentDetailData>
  /** Resolved incidents for the Resolved tab (1-based page). Omit to hide the
   *  tab. `filters` carries the toolbar state — hosts pass it straight through
   *  as query params; a host that ignores it just gets an unfiltered page. */
  getHistory?: (page: number, filters?: IncidentHistoryFilters) => Promise<IncidentHistoryPage>
  /** Open chat seeded with `text` (and a fresh thread, via onAskNew). */
  onAsk: (text: string) => void
  onAskNew?: (text: string) => void
  /** Deterministic "Investigate in chat": receives the whole incident so the
   *  host can send its id structurally (the backend then routes straight into
   *  the alert-investigation flow instead of classifying the seed text).
   *  Hosts that don't wire it fall back to onAskNew/onAsk with the seed. */
  onInvestigate?: (inc: Incident) => void
  onRetry?: () => void
  /** Kick off a fresh investigation. Return the request promise so the button
   *  can show "Investigating…" while it's in flight. */
  onReinvestigate?: (id: string) => Promise<unknown> | void
  /** Save user feedback (importance / rating / notes) for this incident. */
  onFeedback?: (id: string, patch: IncidentFeedback) => Promise<void> | void
  /** From the URL. `affected-hosts` auto-expands the urgent groups in the
   *  feed; `overdue` / `today` arrive with the matching toolbar chip pre-armed. */
  filter?: string | null
  /** Deep link (e.g. `?incident=<id>` from an alert digest email's "Dig
   *  deeper"): auto-expands that incident's card — which opens its AI
   *  investigation — and scrolls it into view. */
  focusId?: string | null
  /** Live SSE trace subscription for a still-running investigation
   *  (`GET /incidents/{id}/stream`). The host implements it with fetch +
   *  parseSSE (auth headers — EventSource can't send them) and returns an
   *  abort function. When wired, "Dig deeper" on an unfinished incident
   *  shows live node progress instead of a static line. */
  streamProgress?: (
    id: string,
    onEvent: (ev: { event: string; data: Record<string, unknown> }) => void,
  ) => () => void
}

// --- helpers -----------------------------------------------------------------

// Labels come from the canonical severity registry — no local copy to drift.
const SEV_LABELS: Record<number, string> = Object.fromEntries(
  ([0, 1, 2, 3, 4, 5] as const).map((s) => [s, SEVERITY[s].label]),
)

// One DISTINCT colour per severity — never two severities sharing a colour.
// Sourced from the canonical severity registry (the monitoring dashboard's own
// palette), same as the Outlook tile, so severity reads identically across
// surfaces and against the dashboard.
const SEV_COLOR: Record<number, string> = Object.fromEntries(
  ([0, 1, 2, 3, 4, 5] as const).map((s) => [s, SEVERITY[s].color]),
)

/** Worst-first [severity, count] pairs from the summary's problems_by_severity.
 *  null when the payload carries none (pre-upgrade backend). */
function problemSeverities(bySev?: Record<string, number> | null): [number, number][] | null {
  if (!bySev) return null
  return Object.entries(bySev)
    .map(([sev, count]) => [Number(sev), count] as [number, number])
    .filter(([sev, count]) => count > 0 && Number.isFinite(sev))
    .sort((a, b) => b[0] - a[0])
}

/** Hover text for the card's meta line. A group hides a span behind one
 *  number — the label answers "when did this last fire?", the tooltip adds
 *  "and how long has it been going on?", which is the thing the old
 *  representative-based label was accidentally showing. */
function metaTitle(inc: Incident): string | undefined {
  if (inc.flap || inc.member_count <= 1) return undefined
  const first = inc.oldest_opened_at ?? inc.opened_at
  const last = latestActivityIso(inc)
  if (!first || !last || first === last) return undefined
  return `Latest of ${inc.member_count} events ${relTime(last)} ago · first fired ${relTime(first)} ago (${stamp(first)})`
}

function relTime(iso?: string | null): string {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000))
  if (s < 60) return 'just now'
  if (s < 3600) return `${Math.floor(s / 60)}m`
  if (s < 86400) return `${Math.floor(s / 3600)}h`
  if (s < 86400 * 7) return `${Math.floor(s / 86400)}d`
  return new Date(iso).toLocaleDateString()
}

function clock(iso?: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
}

// absolute local date + time, e.g. "Jun 20, 2026, 14:03" — for the timestamps block.
function stamp(iso?: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

// best-effort: pull a "94%" / "5.2 GB" value out of an alert name for the affected list.
function valueHint(name?: string | null): string {
  if (!name) return ''
  const m = name.match(/(\d+(?:\.\d+)?\s*%)/) || name.match(/(\d+(?:\.\d+)?\s*[GMT]B)/i)
  return m ? m[1].replace(/\s+/g, '') : ''
}

// Card title is always the TRIGGER name (host-agnostic) — a group is one trigger.
function incTitle(inc: Incident): string {
  return inc.alert_name
}

function hostsAffected(inc: Incident): number {
  return inc.hosts_affected ?? inc.member_count
}

function incSubtitle(inc: Incident): string {
  // A flapping card says what's wrong with it — "3 hosts affected" or a
  // severity word describes a state it isn't holding still in.
  if (inc.flap) {
    const host = inc.host ? `${inc.host} · ` : ''
    return inc.flap.state === 'settled'
      ? `${host}Settled after ${inc.flap.flips} flip${inc.flap.flips === 1 ? '' : 's'}.`
      : `${host}Keeps opening and clearing — won't settle.`
  }
  const hosts = hostsAffected(inc)
  if (hosts > 1) return `${hosts} hosts affected`
  // Single host — even for a multi-event group (per-instance fires folded by
  // the skeleton): name the host; the meta line already carries "N events".
  return `${inc.host ? `${inc.host} · ` : ''}${SEV_LABELS[inc.severity] ?? `Severity ${inc.severity}`}`
}

export function investigateSeed(inc: Incident): string {
  return `Investigate "${inc.alert_name}"${inc.host ? ` on ${inc.host}` : ''} — what's the root cause, is it correlated across hosts, and how do I fix it?`
}

const TIERS: { key: IncidentTier; title: string; tone: string; collapsed?: boolean }[] = [
  { key: 'needs_now', title: 'Needs you now', tone: 'crit' },
  { key: 'worth_a_look', title: 'Worth a look', tone: 'warn' },
  { key: 'routed_to_log', title: 'Routed to log', tone: 'faint', collapsed: true },
]

// --- tier tone icons (match the design) --------------------------------------

function TierIcon({ tier }: { tier: IncidentTier }) {
  if (tier === 'needs_now') return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M7.9 3h8.2L21 7.9v8.2L16.1 21H7.9L3 16.1V7.9z" /><path d="M12 8v4M12 16h.01" /></svg>
  )
  if (tier === 'worth_a_look') return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M10.3 4.3 2.5 18a2 2 0 0 0 1.7 3h15.6a2 2 0 0 0 1.7-3L13.7 4.3a2 2 0 0 0-3.4 0Z" /><path d="M12 9v4M12 17h.01" /></svg>
  )
  if (tier === 'resolved') return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
  )
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></svg>
  )
}

function Chevron() {
  return <svg className="nw-inc-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 6 15 12 9 18" /></svg>
}

// --- flapping (docs/flapping.md) ---------------------------------------------

/** The up/down strip: one block per leg, width proportional to how long that leg
 *  lasted, with a floor so a 3-second blip is still visible. Reading it as a
 *  shape ("green, green, red-red-red-red") is the whole point — the exact
 *  durations live in the title attribute. */
function FlapStrip({ flap }: { flap: FlapInfo }) {
  if (!flap.segments.length) return null
  return (
    <div className="nw-inc-flapstrip" role="img"
      aria-label={`${flap.flips} state changes over the last ${flap.window_minutes} minutes`}>
      {flap.segments.map((s, i) => (
        <span
          key={`${s.at}-${i}`}
          className={`nw-inc-flapseg ${s.state}${s.until ? '' : ' live'}`}
          style={{ flexGrow: Math.max(s.seconds, 1) }}
          title={`${s.state === 'down' ? 'Down' : 'Up'} ${flapDuration(s.seconds)} · ${clock(s.at)}`}
        />
      ))}
    </div>
  )
}

function flapDuration(seconds: number): string {
  if (seconds < 60) return `${Math.max(seconds, 1)}s`
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`
  return `${(seconds / 3600).toFixed(1)}h`
}

function flapWindowLabel(minutes: number): string {
  if (minutes < 60) return `last ${minutes} min`
  const h = minutes / 60
  return `last ${h < 10 ? h.toFixed(1).replace(/\.0$/, '') : Math.round(h)}h`
}

/** Everything the card says about the episode: the strip, its legend, and — while
 *  the episode is holding alerts — the note that explains why this is one card
 *  and not fourteen. */
function FlapPanel({ flap }: { flap: FlapInfo }) {
  const settled = flap.state === 'settled'
  return (
    <div className="nw-inc-flap">
      <FlapStrip flap={flap} />
      <div className="nw-inc-flap-legend">
        <span className="nw-inc-flap-key"><i className="up" />up</span>
        <span className="nw-inc-flap-key"><i className="down" />down</span>
        <span className="nw-inc-flap-count">
          {flap.flips} flip{flap.flips === 1 ? '' : 's'} · {flapWindowLabel(flap.window_minutes)}
        </span>
      </div>
      {flap.held && !settled && (
        <div className="nw-inc-flap-note">
          <InfoIcon />
          <span>
            Held as one alert — no new incident per flip. You'll be emailed once it
            settles, or if it stays unstable.
          </span>
        </div>
      )}
      {settled && (
        <div className="nw-inc-flap-note settled">
          <InfoIcon />
          <span>
            Settled — it stopped flapping{flap.currently_down ? ' but is still down.' : ' and is up.'}
          </span>
        </div>
      )}
    </div>
  )
}

function InfoIcon() {
  return (
    <svg className="nw-inc-flap-note-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" />
    </svg>
  )
}

// --- the shared filter toolbar (Live + Resolved) ------------------------------

/** One time chip. The two tabs pass different ones — Live filters on when a
 *  card OPENED (Overdue / Today), Resolved on when it CLEARED — but the
 *  control, the tag bubbles and the search box are the same on both. */
interface ChipSpec {
  key: string
  label: string
  title: string
  on: boolean
  /** Colour of the armed state — omit for the default amber. `new` matches the
   *  card's New badge, so chip and badge read as one thing. */
  tone?: 'new'
  toggle: () => void
}

const capTag = (s: string) => (s === 'untagged' ? 'Untagged' : s.charAt(0).toUpperCase() + s.slice(1))

function FilterToolbar({ query, onQuery, placeholder, chips, tagOptions, tagFilter, onTag, sort, onSort, period, onPeriod }: {
  query: string
  onQuery: (v: string) => void
  placeholder: string
  chips: ChipSpec[]
  /** [tag, count], busiest first. Empty → the funnel button stays hidden. */
  tagOptions: [string, number][]
  tagFilter: string
  /** Receives the clicked tag, or 'all' when the active bubble is cleared. */
  onTag: (tag: string) => void
  /** Current section order. Omit the pair to hide the control — the Resolved
   *  tab is server-paginated newest-first, so re-ordering the twenty rows in
   *  hand would lie about the rest. */
  sort?: IncidentSort
  onSort?: (mode: IncidentSort) => void
  /** Which window of resolved incidents the tab covers. Omit the pair to hide
   *  the picker — the Live tab has no period (it IS "right now"). 14 presets
   *  don't fit as chips, so this is the one dropdown in the toolbar. */
  period?: PeriodKey
  onPeriod?: (k: PeriodKey) => void
}) {
  const [showFilters, setShowFilters] = useState(false)
  return (
    <>
      <div className="nw-inc-tools">
        <label className="nw-inc-search">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>
          <input
            type="text"
            placeholder={placeholder}
            value={query}
            onChange={(e) => onQuery(e.target.value)}
          />
        </label>
        {period !== undefined && onPeriod && (
          <select
            className="nw-inc-period"
            value={period}
            aria-label="Period"
            title="Only incidents resolved in this period"
            onChange={(e) => onPeriod(e.target.value as PeriodKey)}
          >
            {PERIOD_GROUPS.map((g) => (
              <optgroup key={g.label} label={g.label}>
                {g.keys.map((k) => (
                  <option key={k} value={k}>{PERIOD_LABELS[k]}</option>
                ))}
              </optgroup>
            ))}
          </select>
        )}
        {chips.map((c) => (
          <button
            key={c.key}
            type="button"
            className={`nw-inc-chip${c.on ? ' on' : ''}${c.tone ? ` chip-${c.tone}` : ''}`}
            aria-pressed={c.on}
            title={c.title}
            onClick={c.toggle}
          >
            {c.label}
          </button>
        ))}
        {sort && onSort && (
          <div className="nw-inc-sort" role="group" aria-label="Sort incidents">
            <span className="nw-inc-sort-l">Sort</span>
            {(['recent', 'severity'] as const).map((m) => (
              <button
                key={m}
                type="button"
                className={`nw-inc-sortbtn${sort === m ? ' on' : ''}`}
                aria-pressed={sort === m}
                title={SORT_TITLES[m]}
                onClick={() => onSort(m)}
              >
                {SORT_LABELS[m]}
              </button>
            ))}
          </div>
        )}
        {tagOptions.length > 0 && (
          <button
            type="button"
            className={`nw-inc-chip nw-inc-chip-icon${showFilters || tagFilter !== 'all' ? ' on' : ''}`}
            aria-expanded={showFilters}
            aria-label="More filters"
            title="More filters"
            onClick={() => setShowFilters((v) => !v)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" /></svg>
          </button>
        )}
      </div>
      {showFilters && tagOptions.length > 0 && (
        <div className="nw-inc-filters">
          <span className="nw-inc-filters-l">Tag</span>
          <div className="nw-inc-tagbubbles">
            {tagOptions.map(([t, n]) => (
              <button
                key={t}
                type="button"
                className={`nw-inc-tagbubble${tagFilter === t ? ' on' : ''}`}
                aria-pressed={tagFilter === t}
                onClick={() => onTag(tagFilter === t ? 'all' : t)}
              >
                {capTag(t)} <span className="nw-inc-tagbubble-n">{n}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  )
}

// --- the component -----------------------------------------------------------

export function Incidents(props: IncidentsProps) {
  const [tab, setTab] = useState<'live' | 'history'>('live')
  const activeCount = props.feed?.summary?.active


  return (
    <div className="nw-inc">
      {props.getHistory && (
        <div className="nw-inc-tabs">
          <button className={`nw-inc-tab ${tab === 'live' ? 'on' : ''}`} onClick={() => setTab('live')}>
            Live{typeof activeCount === 'number' ? ` · ${activeCount}` : ''}
          </button>
          <button className={`nw-inc-tab ${tab === 'history' ? 'on' : ''}`} onClick={() => setTab('history')}>
            Resolved
          </button>
        </div>
      )}
      {tab === 'live'
        ? <LiveView props={props} />
        : <HistoryView getHistory={props.getHistory!} props={props} />}
    </div>
  )
}

function LiveView({ props }: { props: IncidentsProps }) {
  const { feed, error, onRetry } = props
  // Toolbar: text search + "New" / "Overdue" / "Today" chips. Overdue and
  // Today use browser-local midnight as the day boundary, evaluated against
  // the GROUP's member dates (newest for Today, oldest for Overdue — see the
  // filter below): Overdue = some member opened before today and the card is
  // still unresolved; Today = some member opened today. New is the badge's own
  // rule (incidentRecency.ts) — unread, or opened in the last hour. The three
  // are mutually exclusive: they answer the same "when" question at different
  // ranges. ?type=new / ?type=overdue / ?type=today — the Outlook pill's
  // targets — land with the matching chip pre-armed; same feed, just filtered.
  const [query, setQuery] = useState('')
  const [newOnly, setNewOnly] = useState(props.filter === 'new')
  const [overdueOnly, setOverdueOnly] = useState(props.filter === 'overdue')
  const [todayOnly, setTodayOnly] = useState(props.filter === 'today')
  // Tag bubbles live behind the toolbar's funnel button, collapsed by default.
  const [tagFilter, setTagFilter] = useState('all')
  // Which way each section is ordered. Persisted, so a reader who prefers
  // severity-first doesn't re-pick it every visit.
  const [sort, setSort] = useState<IncidentSort>(loadSort)
  // The "New" chip filters against a SNAPSHOT taken when it was armed, not the
  // live seen set. Otherwise opening a card while the chip is on would filter
  // that card out from under the reader — mid-read, since opening it also
  // expands the investigation.
  const [filterSeen, setFilterSeen] = useState<SeenState | null>(null)
  // Live, not frozen: only opening a card writes to this store now, so the
  // pill that clears is the one whose card was opened — the reader's own
  // click, taking effect under their eyes. (It used to be frozen because
  // merely rendering the feed marked everything seen, which would have blanked
  // every pill a frame after it appeared.)
  const [seen, setSeen] = useState<SeenState>(loadSeen)
  // The store notifies on every write, so state follows storage instead of
  // each writer remembering to update it — which is also what keeps the
  // dashboard sidebar's unread count in step with this feed.
  useEffect(() => subscribeSeen((ids) => setSeen({ known: true, ids })), [])
  // The feed's currently-active ids. Every write prunes against these, so
  // resolved ids fall out of storage instead of accumulating. Keyed on the ids
  // themselves, so a 30s poll returning the same feed doesn't rewrite storage.
  const activeKey = JSON.stringify(
    (feed?.items ?? []).filter((i) => i.tier !== 'resolved').map((i) => String(i.id)),
  )
  const hasFeed = feed != null
  // First visit only: record what was already there, so a browser with no
  // store doesn't badge the entire feed the moment the first card is opened
  // (which is what would create the store). Never re-baselines.
  useEffect(() => {
    if (!hasFeed) return
    seedSeen(JSON.parse(activeKey) as string[])
  }, [hasFeed, activeKey])
  // Opening a card is what marks it read. Per browser: a colleague watching
  // the same feed still sees it as new.
  const onOpened = useCallback((id: string) => {
    markSeen(id, JSON.parse(activeKey) as string[])
  }, [activeKey])
  if (error) {
    return (
      <div className="nw-inc-empty">
        <div className="nw-inc-empty-t">Couldn’t load incidents</div>
        <div className="nw-inc-empty-s">{error}</div>
        {onRetry && <button className="nw-inc-btn" onClick={onRetry}>Retry</button>}
      </div>
    )
  }
  if (!feed) {
    // No feed and no error means we haven't loaded yet (hosts may render one
    // frame before their fetch effect fires) — never assert "all clear" here.
    return <div className="nw-inc-empty"><div className="nw-inc-empty-s">Loading incidents…</div></div>
  }
  // Browser-local midnight — same "day" the Outlook strip and the backend's
  // NETWATCH_DAY_TZ windows use.
  const dayStart = new Date().setHours(0, 0, 0, 0)
  const q = query.trim().toLowerCase()
  const all = feed.items ?? []
  const ts = (v?: string | null) => (v ? new Date(v).getTime() : NaN)
  const items = all.filter((it) => {
    // Grouped cards carry the span of their members' opened_at values; the
    // card's own opened_at is just the representative's (often the OLDEST
    // host on a long-lived trigger). Overdue = the oldest member predates
    // today and the card is unresolved; Today = the newest member opened
    // today — so a week-old ping card that caught two new hosts this morning
    // shows under Today instead of vanishing behind its rep's date.
    // New reads the SAME rule as the card badge, so what the chip selects is
    // exactly what wears the pill.
    if (newOnly && !isNewIncident(it, filterSeen ?? seen)) return false
    if (overdueOnly) {
      if (it.resolved_at) return false
      const opened = ts(it.oldest_opened_at ?? it.opened_at)
      if (!Number.isFinite(opened) || opened >= dayStart) return false
    }
    if (todayOnly) {
      const opened = ts(it.newest_opened_at ?? it.opened_at)
      if (!Number.isFinite(opened) || opened < dayStart) return false
    }
    if (tagFilter !== 'all' && (it.tag ?? 'untagged') !== tagFilter) return false
    if (q && !`${it.alert_name ?? ''} ${it.host ?? ''}`.toLowerCase().includes(q)) return false
    return true
  })
  const filtering = newOnly || overdueOnly || todayOnly || tagFilter !== 'all' || q.length > 0
  // Counted over the UNFILTERED feed — the chip says how many are new, not how
  // many survive whatever else is armed.
  const newIds = new Set(all.filter((it) => isNewIncident(it, seen)).map((it) => String(it.id)))
  const newCount = newIds.size
  // Tag options come from the UNFILTERED feed so counts don't collapse into
  // the current selection; AI-coined tags surface here without a registry.
  const tagCounts = new Map<string, number>()
  for (const it of all) {
    const t = it.tag ?? 'untagged'
    tagCounts.set(t, (tagCounts.get(t) ?? 0) + 1)
  }
  const tagOptions: [string, number][] = [...tagCounts.entries()].sort((a, b) => b[1] - a[1])
  // Tiers are the triage order and never move; `sort` orders the cards WITHIN
  // each section. The backend hands the feed back severity-first, so ordering
  // here (rather than asking the server) keeps the toggle instant and the feed
  // cacheable.
  const byTier: Record<IncidentTier, Incident[]> = { needs_now: [], worth_a_look: [], routed_to_log: [], resolved: [] }
  for (const it of items) (byTier[it.tier] ?? byTier.worth_a_look).push(it)
  for (const k of Object.keys(byTier) as IncidentTier[]) byTier[k] = sortIncidents(byTier[k], sort)
  return (
    <>
      <SummaryBanner summary={feed.summary} />
      <FilterToolbar
        query={query}
        onQuery={setQuery}
        placeholder="Search incidents or hosts…"
        chips={[
          {
            key: 'new',
            label: newCount > 0 ? `New · ${newCount}` : 'New',
            title: `Only incidents opened in the last ${NEW_WINDOW_MINUTES} minutes that are still open`,
            on: newOnly,
            tone: 'new',
            toggle: () => setNewOnly((v) => {
              if (!v) { setOverdueOnly(false); setTodayOnly(false); setFilterSeen(seen) }
              else setFilterSeen(null)
              return !v
            }),
          },
          {
            key: 'overdue',
            label: 'Overdue',
            title: 'Only incidents opened before today that are still unresolved',
            on: overdueOnly,
            toggle: () => setOverdueOnly((v) => {
              if (!v) { setTodayOnly(false); setNewOnly(false) }
              return !v
            }),
          },
          {
            key: 'today',
            label: 'Today',
            title: 'Only incidents opened today',
            on: todayOnly,
            toggle: () => setTodayOnly((v) => {
              if (!v) { setOverdueOnly(false); setNewOnly(false) }
              return !v
            }),
          },
        ]}
        tagOptions={tagOptions}
        tagFilter={tagFilter}
        onTag={setTagFilter}
        sort={sort}
        onSort={(m) => setSort(saveSort(m))}
      />
      {items.length === 0 && (
        filtering
          ? <EmptyState kind="search" variant="filter" title="No matching incidents"
              body="Nothing matches the current search and filters."
              action={{
                label: 'Clear filters',
                onClick: () => {
                  setQuery(''); setNewOnly(false); setOverdueOnly(false); setTodayOnly(false); setTagFilter('all')
                },
              }} />
          : <EmptyState kind="shield" variant="positive" title="All clear"
              body="No active incidents right now. New alerts land here the moment Zabbix raises them." />
      )}
      {TIERS.map((t) => byTier[t.key].length > 0 && (
        <TierSection key={t.key} title={t.title} tone={t.tone} count={byTier[t.key].length}
          collapsed={t.collapsed} items={byTier[t.key]} newIds={newIds}
          onOpened={onOpened} props={props} />
      ))}
    </>
  )
}

function HistoryView({ getHistory, props }: {
  getHistory: (page: number, filters?: IncidentHistoryFilters) => Promise<IncidentHistoryPage>
  props: IncidentsProps
}) {
  // Same toolbar as Live, but every filter is SERVER-side: the tab is
  // paginated, so filtering the twenty rows in hand would hide matches sitting
  // on page 5. The period picker bounds WHICH resolved incidents the grouped
  // cards cover; "Aged" (cleared only after sitting open a full day) is a
  // duration filter, orthogonal to the period, so it stays a chip. Live's
  // "Overdue" needs an unresolved row and can't mean anything here.
  const [page, setPage] = useState(1)
  const [query, setQuery] = useState('')
  const [search, setSearch] = useState('')          // `query`, debounced
  const [period, setPeriod] = useState<PeriodKey>(() => loadPeriod())
  const [aged, setAged] = useState(false)
  const [tagFilter, setTagFilter] = useState('all')
  const [data, setData] = useState<IncidentHistoryPage | null>(null)
  // Facets outlive a page load so the bubbles don't blink (or vanish, when the
  // active filter narrows the set) while the next page is in flight.
  const [tagOptions, setTagOptions] = useState<[string, number][]>([])
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  // One request per typing pause, not per keystroke. Any filter change also
  // rewinds to page 1 — page 4 of the old result set means nothing now.
  useEffect(() => {
    const t = setTimeout(() => { setSearch(query.trim()); setPage(1) }, 300)
    return () => clearTimeout(t)
  }, [query])

  // The filters the on-screen data was actually FETCHED with — captured with
  // the response, not recomputed at render, so an expanded card's member list
  // reads the SAME instants/filters the card's counts came from (a tab left
  // open across midnight would otherwise drift).
  const [activeFilters, setActiveFilters] = useState<IncidentHistoryFilters | null>(null)

  useEffect(() => {
    let alive = true
    setLoading(true); setErr(null)
    const filters: IncidentHistoryFilters = {}
    if (search) filters.q = search
    if (tagFilter !== 'all') filters.tag = tagFilter
    if (aged) filters.window = 'aged'
    const b = resolvePeriod(period, new Date())
    if (b.from) filters.from = b.from
    if (b.to) filters.to = b.to
    getHistory(page, filters)
      .then((d) => {
        if (!alive) return
        setData(d)
        setActiveFilters(filters)
        if (d.tag_counts) {
          setTagOptions(Object.entries(d.tag_counts).filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1]))
        }
      })
      .catch((e) => { if (alive) setErr((e as Error)?.message || 'Failed to load history') })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [getHistory, page, search, aged, tagFilter, period])

  const filtering = search.length > 0 || aged || tagFilter !== 'all' || period !== DEFAULT_PERIOD
  const clearFilters = () => {
    setQuery(''); setSearch(''); setAged(false); setTagFilter('all')
    setPeriod(savePeriod(DEFAULT_PERIOD)); setPage(1)
  }
  const toolbar = (
    <FilterToolbar
      query={query}
      onQuery={setQuery}
      placeholder="Search resolved incidents or hosts…"
      period={period}
      onPeriod={(k) => { setPeriod(savePeriod(k)); setPage(1) }}
      chips={[
        {
          key: 'aged',
          label: 'Aged',
          title: 'Only incidents that stayed open a day or more before they cleared',
          on: aged,
          toggle: () => { setAged((v) => !v); setPage(1) },
        },
      ]}
      tagOptions={tagOptions}
      tagFilter={tagFilter}
      onTag={(t) => { setTagFilter(t); setPage(1) }}
    />
  )

  // Cards on this tab are RESOLVED trigger groups — their member list must
  // come from the same period AND filters the card was built over, not from
  // the live (unresolved) set the plain getMembers reads.
  const mf = activeFilters
  const cardProps: IncidentsProps = {
    ...props,
    getMembers: (id) => props.getMembers(id, {
      resolved: true, from: mf?.from, to: mf?.to,
      q: mf?.q, tag: mf?.tag, aged: mf?.window === 'aged',
    }),
  }
  // Cards cache their member list; the fingerprint in the key remounts them
  // when the fetched filter set changes, so a list loaded under "Last 30 days"
  // can't sit open under a card recounted for "Today".
  const mfKey = [mf?.from, mf?.to, mf?.q, mf?.tag, mf?.window].join('|')

  // The toolbar stays mounted through every state below — unmounting it would
  // drop focus mid-search on each debounced reload.
  if (err) {
    return (
      <>
        {toolbar}
        <div className="nw-inc-empty">
          <div className="nw-inc-empty-t">Couldn’t load history</div>
          <div className="nw-inc-empty-s">{err}</div>
        </div>
      </>
    )
  }
  if (!data) {
    return <>{toolbar}<div className="nw-inc-empty"><div className="nw-inc-empty-s">Loading history…</div></div></>
  }
  if (data.items.length === 0) {
    return (
      <>
        {toolbar}
        {filtering
          ? <EmptyState kind="search" variant="filter" title="No matching incidents"
              body="No resolved incident matches the current period, search and filters."
              action={{ label: 'Clear filters', onClick: clearFilters }} />
          : <EmptyState kind="shield" variant="info" title="No resolved incidents yet"
              body="Resolved alerts move here once Zabbix clears them." />}
      </>
    )
  }

  const pageCount = Math.max(1, Math.ceil(data.total / (data.page_size || 20)))
  // Pre-upgrade backends send neither events_total nor grouped cards — then
  // the header falls back to the plain total.
  const events = data.events_total ?? null
  return (
    <>
      {toolbar}
      <div className="nw-inc-sec"><div className="nw-inc-sec-head tone-good" style={{ cursor: 'default' }}>
        <span className="nw-inc-sec-title">Resolved · {PERIOD_LABELS[period]}</span>
        <span className="nw-inc-sec-count">
          {events !== null && events !== data.total
            ? `${data.total} group${data.total === 1 ? '' : 's'} · ${events} event${events === 1 ? '' : 's'}`
            : data.total}
        </span>
      </div></div>
      {data.truncated && (
        <div className="nw-inc-trunc">
          Large period — showing the newest {(events ?? 0).toLocaleString()} events; narrow the period for the rest.
        </div>
      )}
      {data.items.map((inc) => <IncidentCard key={`${inc.id}:${mfKey}`} inc={inc} tone="good" props={cardProps} />)}
      {pageCount > 1 && (
        <div className="nw-inc-pager">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading}>‹ Prev</button>
          <span>Page {page} of {pageCount}</span>
          <button onClick={() => setPage((p) => Math.min(pageCount, p + 1))} disabled={page >= pageCount || loading}>Next ›</button>
        </div>
      )}
    </>
  )
}

function SummaryBanner({ summary }: { summary?: IncidentFeedSummary | null }) {
  if (!summary) return null
  const sevs = problemSeverities(summary.problems_by_severity)

  if (sevs) {
    // Zabbix-mirroring banner: the headline is the WORST severity present and
    // its problem count; the sub-line lists every non-empty severity with its
    // own count and colour — problems, not trigger-groups, never combined.
    const worst = sevs[0] ?? null
    const total = sevs.reduce((s, [, n]) => s + n, 0)
    // Icon shape by urgency; icon/border COLOUR is the worst severity's own
    // (a High-only feed reads orange, not disaster red).
    const tone = !worst ? 'good' : worst[0] >= 4 ? 'crit' : worst[0] >= 2 ? 'warn' : 'good'
    const toneColor = worst ? SEV_COLOR[worst[0]] : undefined
    const headline = worst
      ? `${worst[1]} ${(SEV_LABELS[worst[0]] ?? `severity ${worst[0]}`).toLowerCase()}`
      : 'All clear'
    return (
      <div
        className={`nw-inc-banner${tone === 'good' ? ' tone-good' : ''}`}
        style={toneColor ? { borderColor: `color-mix(in srgb, ${toneColor} 40%, transparent)` } : undefined}
      >
        <span
          className={`nw-inc-mark tone-${tone}`}
          style={toneColor ? ({ '--tone': toneColor } as React.CSSProperties) : undefined}
        ><TierIcon tier={tone === 'crit' ? 'needs_now' : tone === 'warn' ? 'worth_a_look' : 'resolved'} /></span>
        <div className="nw-inc-banner-b">
          <div className="nw-inc-banner-h">{headline}</div>
          <div className="nw-inc-banner-s nw-inc-sevrow">
            {sevs.map(([sev, n]) => (
              <span className="nw-inc-sev" key={sev} title={`${n} active problem${n === 1 ? '' : 's'} at ${SEV_LABELS[sev] ?? sev}`}>
                <i style={{ background: SEV_COLOR[sev] ?? '#8b98a8' }} />
                {n} {(SEV_LABELS[sev] ?? `severity ${sev}`).toLowerCase()}
              </span>
            ))}
            <span className="nw-inc-sev-total">{total} problem{total === 1 ? '' : 's'} · {summary.active} group{summary.active === 1 ? '' : 's'}</span>
          </div>
        </div>
      </div>
    )
  }

  // Pre-upgrade backend (no per-severity counts) — the old tier banner.
  const needs = summary.needs_now
  const tone = needs > 0 ? 'crit' : summary.active > 0 ? 'warn' : 'good'
  const headline = needs > 0
    ? `${needs} need${needs === 1 ? 's' : ''} you now`
    : summary.active > 0 ? `${summary.active} active · nothing urgent` : 'All clear'
  return (
    <div className={`nw-inc-banner tone-${tone}`}>
      <span className={`nw-inc-mark tone-${tone}`}><TierIcon tier={needs > 0 ? 'needs_now' : summary.active > 0 ? 'worth_a_look' : 'resolved'} /></span>
      <div className="nw-inc-banner-b">
        <div className="nw-inc-banner-h">{headline}</div>
        <div className="nw-inc-banner-s">
          {summary.active} active · {summary.events_bundled} event{summary.events_bundled === 1 ? '' : 's'} bundled · {summary.routed_to_log} routed to log
        </div>
      </div>
    </div>
  )
}

function TierSection({ title, tone, count, collapsed, items, newIds, onOpened, props }: {
  title: string; tone: string; count: number; collapsed?: boolean; items: Incident[]
  /** Ids wearing the "New" pill — decided once in LiveView so the badge and
   *  the toolbar's New chip can't disagree. */
  newIds: Set<string>
  /** Called with the incident's id the first time its card is opened. */
  onOpened?: (id: string) => void
  props: IncidentsProps
}) {
  const [open, setOpen] = useState(!collapsed)
  // Section header colour = the WORST severity actually inside it — a
  // High-only "Needs you now" reads High orange, not disaster red. The log
  // tier keeps its faint tone (severity colour there would shout).
  const worstSev = items.reduce((m, i) => Math.max(m, i.severity ?? 0), 0)
  const sevTone = tone !== 'faint' && items.length > 0
    ? ({ '--tone': SEV_COLOR[worstSev] } as React.CSSProperties)
    : undefined
  return (
    <div className="nw-inc-sec">
      <button className={`nw-inc-sec-head tone-${tone}`} style={sevTone} onClick={() => setOpen((o) => !o)}>
        {collapsed && <span className={`nw-inc-sec-chev ${open ? 'open' : ''}`}><Chevron /></span>}
        {tone === 'crit' && <span className="nw-inc-pulse" />}
        <span className="nw-inc-sec-title">{title}</span>
        <span className="nw-inc-sec-count">{count}</span>
      </button>
      {open && items.map((inc) => (
        <IncidentCard key={inc.id} inc={inc} tone={tone} isNew={newIds.has(String(inc.id))}
          onOpened={onOpened} props={props} />
      ))}
    </div>
  )
}

function IncidentCard({ inc, tone, isNew, onOpened, props }: {
  inc: Incident; tone: string
  /** Wear the "New" pill. Decided by the view (LiveView) rather than here, so
   *  one rule feeds both the badge and the New chip. Resolved-tab cards never
   *  pass it — nothing closed is new. */
  isNew?: boolean
  /** Reports the first open of this card, which is what clears its "New" pill. */
  onOpened?: (id: string) => void
  props: IncidentsProps
}) {
  // Deep-linked from outside (an alert email's "Dig deeper"): open the card —
  // which loads the AI investigation — and scroll it into view.
  const focused = props.focusId != null && String(inc.id) === String(props.focusId)
  const wantOpen = focused
    || (props.filter === 'affected-hosts' && inc.tier === 'needs_now' && hostsAffected(inc) > 1)
  const [open, setOpen] = useState(wantOpen)
  const [members, setMembers] = useState<Incident[] | null>(null)
  const [memErr, setMemErr] = useState<string | null>(null)
  const [detail, setDetail] = useState<IncidentDetailData | null>(null)
  const [showDetail, setShowDetail] = useState(focused)
  const [detailLoading, setDetailLoading] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)

  const grouped = inc.member_count > 1

  useEffect(() => {
    if (!focused) return
    // Landing here from an alert email's "Dig deeper" is a read, same as a
    // click — the card is open and its investigation is on screen.
    onOpened?.(String(inc.id))
    void loadDetail()
    if (grouped) {
      props.getMembers(inc.id)
        .then(setMembers)
        .catch((e) => setMemErr((e as Error)?.message || 'Failed to load'))
    }
    cardRef.current?.scrollIntoView({ block: 'center' })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only deep link
  }, [])

  async function loadDetail() {
    if (detail !== null || detailLoading) return
    setDetailLoading(true)
    try { setDetail(await props.getDetail(inc.id)) } catch { /* keep null → "no analysis" */ }
    finally { setDetailLoading(false) }
  }
  async function toggle() {
    const next = !open
    setOpen(next)
    if (next) {
      // Opening the card is what marks it read — this is the click that
      // clears its "New" pill (for THIS browser; a colleague's feed is
      // untouched). Collapsing it again does not un-read it.
      onOpened?.(String(inc.id))
      // Expanding the card also opens the investigation detail ("Dig deeper").
      setShowDetail(true)
      void loadDetail()
      if (grouped && members === null) {
        try { setMembers(await props.getMembers(inc.id)) } catch (e) { setMemErr((e as Error)?.message || 'Failed to load') }
      }
    }
  }
  function digDeeper() {
    const next = !showDetail
    setShowDetail(next)
    if (next) void loadDetail()
  }
  const ask = () =>
    props.onInvestigate
      ? props.onInvestigate(inc)
      : (props.onAskNew ?? props.onAsk)(investigateSeed(inc))

  // Every member of the trigger group (the parent + its members), newest
  // first — each is investigated on its own, so each row can "Dig deeper"
  // into its report.
  const allMembers = members ? [inc, ...members].sort((a, b) => (b.opened_at || '').localeCompare(a.opened_at || '')) : [inc]
  // Since instance-skeleton grouping, members ≠ hosts: per-instance fires on
  // ONE server fold into one card. A same-host group must not claim
  // "Affected hosts (4)" over four identical rows — count distinct hosts and
  // let the rows show what actually differs (the per-instance alert name).
  const distinctHosts = new Set(allMembers.map((m) => m.host).filter(Boolean)).size || 1
  const sameHost = distinctHosts === 1 && allMembers.length > 1

  // The card wears its OWN severity: the registry icon (shape + colour) and
  // the accent/left-edge colour come from inc.severity, never from the tier —
  // a High card is orange even inside "Needs you now".
  const sevTone = { '--tone': SEV_COLOR[inc.severity ?? 0] } as React.CSSProperties

  return (
    <div ref={cardRef} className={`nw-inc-card tone-${tone} ${open ? 'open' : ''}`} style={sevTone}>
      <button className="nw-inc-card-head" onClick={toggle} aria-expanded={open}>
        <span className={`nw-inc-card-chev ${open ? 'open' : ''}`}><Chevron /></span>
        <span className="nw-inc-ic"><SeverityIcon level={inc.severity} size={17} /></span>
        <span className="nw-inc-card-b">
          <span className="nw-inc-card-t">{incTitle(inc)}</span>
          <span className="nw-inc-card-s">{incSubtitle(inc)}</span>
        </span>
        <span className="nw-inc-card-r">
          {inc.flap
            // The flap state IS the headline while it lasts — it outranks
            // "New" (a flapping card is loud enough already) and keeps the
            // pill slot to one badge.
            ? <span className={`nw-inc-flappill ${inc.flap.state}`}>
                {inc.flap.state === 'settled' ? 'Settled' : <><i />Flapping</>}
              </span>
            : isNew
              ? <span className="nw-inc-new" title={`New since your last visit, or opened in the last ${NEW_WINDOW_MINUTES} minutes`}><i />New</span>
              : null}
          <span className="nw-inc-card-meta" title={metaTitle(inc)}>
            {inc.flap
              ? `${inc.flap.flips} flip${inc.flap.flips === 1 ? '' : 's'}`
              // The card's age is its LAST activity, which is what it sorts on
              // — for a group that's the newest member, not the representative
              // (the earliest to fire, so much older). Showing the rep's time
              // here made a freshly-active group read "3h" while sitting above
              // a card reading "29m". `latestActivityIso` is the shared clock.
              : `${inc.member_count} event${inc.member_count === 1 ? '' : 's'} · ${relTime(latestActivityIso(inc))}`}
          </span>
        </span>
      </button>

      {/* Outside the collapsed/expanded split on purpose: "won't settle" is a
          shape you read at a glance, not something to click for. */}
      {inc.flap && <FlapPanel flap={inc.flap} />}

      {open && (
        <div className="nw-inc-card-body">
          {grouped && (
            <>
              {memErr && <div className="nw-inc-muted">⚠ {memErr}</div>}
              {!memErr && members === null && <div className="nw-inc-muted">Loading group members…</div>}
              {members && members.length > 0 && (
                <>
                  <div className="nw-inc-h">
                    {inc.flap
                      // The members of a flap group are the SAME alert firing
                      // again, so "Alerts on this host" undersells what the
                      // list is: the flips themselves, newest first.
                      ? `Flips (${allMembers.length})`
                      : sameHost
                        ? `Alerts on this host (${allMembers.length})`
                        : `Affected hosts (${distinctHosts})`}
                  </div>
                  <div className="nw-inc-hosts">
                    {allMembers.map((m) => (
                      <HostRow key={m.id} host={m} isParent={m.id === inc.id} sameHost={sameHost} props={props} />
                    ))}
                  </div>
                </>
              )}
            </>
          )}

          <div className="nw-inc-actions">
            {/* Grouped: each host row has its own Dig deeper, so no card-level one. */}
            {!grouped && <button className="nw-inc-dig" onClick={digDeeper}>{showDetail ? 'Hide details' : 'Dig deeper →'}</button>}
            <button className="nw-inc-chat" onClick={ask}>Investigate in chat →</button>
          </div>

          {!grouped && showDetail && (
            detailLoading && detail === null
              ? <div className="nw-inc-muted">Loading the investigation…</div>
              : <CaseReportView detail={detail} onReinvestigate={props.onReinvestigate} onFeedback={props.onFeedback} streamProgress={props.streamProgress} />
          )}
        </div>
      )}
    </div>
  )
}

// One member inside a trigger group: its host name (or, when every member is
// on the SAME host, its own alert name — that's what distinguishes per-DB /
// per-service instance fires) + a "Dig deeper" that loads THAT member's own
// investigation (each member is investigated individually).
function HostRow({ host, isParent, sameHost, props }: { host: Incident; isParent: boolean; sameHost?: boolean; props: IncidentsProps }) {
  const [show, setShow] = useState(false)
  const [detail, setDetail] = useState<IncidentDetailData | null>(null)
  const [loading, setLoading] = useState(false)

  async function dig() {
    const next = !show
    setShow(next)
    if (next && detail === null && !loading) {
      setLoading(true)
      try { setDetail(await props.getDetail(host.id)) } catch { /* keep null */ }
      finally { setLoading(false) }
    }
  }

  const hint = valueHint(host.alert_name)
  const label = sameHost ? (host.alert_name || host.host || '—') : (host.host || '—')
  return (
    <div className={`nw-inc-hostrow ${show ? 'open' : ''}`}>
      <div className="nw-inc-hostrow-head">
        <span className="an">{label}{isParent && <span className="nw-inc-hostrow-tag">first</span>}</span>
        <span className="as">{hint || (SEV_LABELS[host.severity] ?? '')} · {clock(host.opened_at)}</span>
        <button className="nw-inc-dig" onClick={dig}>{show ? 'Hide' : 'Dig deeper →'}</button>
      </div>
      {show && (
        loading && detail === null
          ? <div className="nw-inc-muted">Loading the investigation…</div>
          : <CaseReportView detail={detail} onReinvestigate={props.onReinvestigate} onFeedback={props.onFeedback} streamProgress={props.streamProgress} />
      )}
    </div>
  )
}

// Live investigation progress for a still-running incident: subscribes to the
// host-provided SSE trace stream and renders the last few events. Renders a
// waiting line until the first event arrives (pre-R5 investigations emit few
// trace events — the stream fills in as the flow gains emits).
function LiveProgress({ id, stream }: {
  id: string
  stream: NonNullable<IncidentsProps['streamProgress']>
}) {
  const [lines, setLines] = useState<string[]>([])
  useEffect(() => {
    const abort = stream(id, (ev) => {
      if (ev.event !== 'trace') return
      const d = ev.data as { kind?: string; name?: string; phase?: string }
      const label = [d.kind, d.name].filter(Boolean).join(': ')
      if (!label) return
      setLines((prev) => {
        if (prev[prev.length - 1] === label) return prev
        return [...prev, label].slice(-6)
      })
    })
    return abort
  }, [id, stream])
  return (
    <div className="nw-inc-live">
      <div className="nw-inc-muted">
        <span className="nw-inc-live-dot" aria-hidden="true" /> Investigating — live progress:
      </div>
      {lines.length === 0
        ? <div className="nw-inc-live-line muted">Waiting for investigation events…</div>
        : lines.map((l, i) => <div className="nw-inc-live-line" key={i}>{l}</div>)}
    </div>
  )
}

// Verdict badge copy per the code-derived verdict_state (R4). Absent on
// legacy reports → no badge (the old validity-only world had no honest label).
const VERDICT_BADGE: Record<string, { label: string; cls: string }> = {
  confirmed: { label: 'Cause confirmed', cls: 'v-confirmed' },
  probable: { label: 'Probable cause', cls: 'v-probable' },
  inconclusive: { label: 'Inconclusive', cls: 'v-inconclusive' },
}

function CaseReportView({ detail, onReinvestigate, onFeedback, streamProgress }: {
  detail: IncidentDetailData | null
  onReinvestigate?: (id: string) => Promise<unknown> | void
  onFeedback?: (id: string, patch: IncidentFeedback) => Promise<void> | void
  streamProgress?: IncidentsProps['streamProgress']
}) {
  if (!detail) {
    return <EmptyState compact kind="search" variant="info" title="No investigation yet"
      body="NetWatch hasn't produced a case report for this incident." />
  }
  const cr = detail.case_report
  const co = detail.correlation
  const verdict = cr?.verdict_state ? VERDICT_BADGE[cr.verdict_state] : null
  const peerCount = detail.report_inherited_from
    ? Math.max((detail.group_size ?? 1) - 1, 1)
    : 0
  return (
    <div className="nw-inc-detail">
      {/* R6 group inheritance: this member wasn't investigated on its own —
          the report below is the group parent's. */}
      {detail.report_inherited_from && (
        <div className="nw-inc-inherited">
          Grouped with {peerCount} peer{peerCount === 1 ? '' : 's'} — showing
          the group&apos;s investigation.
        </div>
      )}
      {/* The verdict, styled as the headline — cr.statement was persisted but
          never rendered before R4. */}
      {cr && (
        <div className="nw-inc-headline">
          {verdict && <span className={`nw-inc-verdict ${verdict.cls}`}>{verdict.label}</span>}
          <span className="nw-inc-headline-t">{cr.statement}</span>
        </div>
      )}
      {/* When the alert fired / was ingested / cleared */}
      {(detail.opened_at || detail.created_at || detail.resolved_at) && (
        <div className="nw-inc-meta">
          {detail.opened_at && (
            <div><b>Fired at:</b> {stamp(detail.opened_at)} <span className="h">({relTime(detail.opened_at)})</span></div>
          )}
          {detail.created_at && (
            <div><b>Received:</b> {stamp(detail.created_at)} <span className="h">({relTime(detail.created_at)})</span></div>
          )}
          {detail.resolved_at && (
            <div><b>Resolved:</b> {stamp(detail.resolved_at)} <span className="h">({relTime(detail.resolved_at)})</span></div>
          )}
        </div>
      )}

      {/* ONE card (amendment 20). The block data carries every deterministic
          section — pattern strip, recurring behaviour, processes, slate,
          other open problems, what-would-settle-it — and the model's reading
          (remediation, caveats) renders inside the frame as the footer. */}
      {detail.findings && (
        <FindingsBlock data={detail.findings} footer={cr ? <ModelReading cr={cr} /> : undefined} />
      )}

      {/* Card-less (legacy, pre-findings) incidents: the original section
          layout. Everything here is inside the card when one exists. */}
      {cr ? (
        !detail.findings && (
          <>
            {cr.validated_claims && cr.validated_claims.length > 0 && (
              <><div className="nw-inc-h">Validated claims</div>
                <div className="nw-inc-claims">
                  {cr.validated_claims.map((c, i) => (
                    <div className="nw-inc-claim" key={i}>
                      <div className="c">{c.claim}</div>
                      {c.evidence && <div className="e">↪ {c.evidence}</div>}
                    </div>
                  ))}
                </div></>
            )}
            {/* Not on an inconclusive verdict: the chain narrates one branch
                as settled when the deciding check said it isn't (the reason
                R4 clears remediation there). Legacy reports (no
                verdict_state) keep it. */}
            {cr.causal_chain && cr.causal_chain.length > 0 && cr.verdict_state !== 'inconclusive' && (
              <><div className="nw-inc-h">Causal chain</div>
                <ol className="nw-inc-chain">{cr.causal_chain.map((s, i) => <li key={i}>{s}</li>)}</ol></>
            )}
            {cr.remediation_steps && cr.remediation_steps.length > 0 && (
              <><div className="nw-inc-h">Remediation</div>
                <ul className="nw-inc-rem">{cr.remediation_steps.map((s, i) => <li key={i}>{s}</li>)}</ul></>
            )}
            {cr.ruled_out && cr.ruled_out.length > 0 && (
              <><div className="nw-inc-h">Ruled out</div>
                <ul className="nw-inc-rem nw-inc-ruled">{cr.ruled_out.map((s, i) => <li key={i}>{s}</li>)}</ul></>
            )}
          </>
        )
      ) : streamProgress
        ? <LiveProgress id={detail.id} stream={streamProgress} />
        : <div className="nw-inc-muted">No case report yet — the investigation may still be running.</div>}

      {/* Correlation — card-less incidents only: the card carries peer scope
          and recurrence in its slate and the other open problems as its own
          section. */}
      {co && !detail.findings && (
        <><div className="nw-inc-h">Correlation</div>
          <div className="nw-inc-corr">
            {co.peers && co.peers.length > 0
              ? <div><b>{co.peers.length}</b> peer host{co.peers.length === 1 ? '' : 's'} with the same trigger: {co.peers.slice(0, 5).map((p) => p.host).join(', ')}</div>
              : <div className="muted">Isolated — not firing on other hosts.</div>}
            {co.host_other_problems && co.host_other_problems.length > 0 && (
              <div>
                <b>{co.host_other_problems.length}</b> other open problem{co.host_other_problems.length === 1 ? '' : 's'} on this host:
                <ul className="nw-inc-rem">{co.host_other_problems.slice(0, 5).map((p, i) => <li key={i}>{p.name}</li>)}</ul>
              </div>
            )}
            <div>Recurred <b>{co.recurrence_24h ?? 0}×</b> in 24h · <b>{co.recurrence_7d ?? 0}×</b> in 7d.</div>
          </div></>
      )}

      {/* Actions + feedback — separate from the narrative */}
      {(onFeedback || onReinvestigate) && (
        <FeedbackAndActions detail={detail} onReinvestigate={onReinvestigate} onFeedback={onFeedback} />
      )}
    </div>
  )
}

/** The diagnosis' reading, rendered inside the card frame (amendment 20):
 *  remediation only. Deliberately NOT block data — the block stays
 *  deterministic; this is the model's layer, demarcated as the card footer.
 *  The chain, the claim lists and the "Unverified claims" caveats stay out
 *  entirely (amendment 23): the verdict, slate and statement already carry
 *  that story, and the caveats read as hedging noise to the operator. */
function ModelReading({ cr }: { cr: CaseReport }) {
  const remediation = cr.remediation_steps ?? []
  if (remediation.length === 0) return null
  return (
    <>
      <h4 className="nw-fb-h">Remediation</h4>
      <ul className="nw-fb-lines">{remediation.map((s, i) => <li key={i}>{s}</li>)}</ul>
    </>
  )
}

function FeedbackAndActions({ detail, onReinvestigate, onFeedback }: {
  detail: IncidentDetailData
  onReinvestigate?: (id: string) => Promise<unknown> | void
  onFeedback?: (id: string, patch: IncidentFeedback) => Promise<void> | void
}) {
  // Rating + notes only make sense on a FINISHED investigation — no case
  // report yet (still running / never ran) → nothing to rate.
  const investigated = !!detail.case_report
  // Thumbs up / down → the 1–5 response_rating (up = 5, down = 1); saves on click.
  // Once feedback is given (already rated, or just voted) the buttons disappear.
  // Read-back boundary: ≥4 shows 👍, anything rated below shows 👎 — the UI
  // only ever writes 1 or 5, so a 2–3 (API-set) reads back as 👎. This is the
  // same boundary the episodic backfill's confirmed_by_user uses (≥4).
  const initial: 'up' | 'down' | null =
    (detail.response_rating ?? 0) >= 4 ? 'up' : (detail.response_rating ?? 0) > 0 ? 'down' : null
  const [vote, setVote] = useState<'up' | 'down' | null>(initial)
  const [saving, setSaving] = useState(false)
  // Importance 1–5 → user_importance. A triage dial, not feedback: the feed
  // tiers on max-of(user_importance ?? severity), so 5 promotes the card to
  // "needs you now" and 1–2 routes it below the fold. Always re-settable.
  const [importance, setImportance] = useState<number | null>(detail.user_importance ?? null)
  const [impSaving, setImpSaving] = useState(false)

  async function setPriority(n: number) {
    if (!onFeedback || impSaving || n === importance) return
    setImpSaving(true)
    try {
      await onFeedback(detail.id, { user_importance: n })
      setImportance(n)
    } catch { /* keep the old selection so they can retry */ }
    finally { setImpSaving(false) }
  }
  // Free-text feedback on the investigation (saved to user_feedback).
  const [note, setNote] = useState('')
  const [noteState, setNoteState] = useState<'idle' | 'saving' | 'sent'>(
    detail.user_feedback ? 'sent' : 'idle',
  )
  // Stays "Investigating…" after a successful kick-off — the button re-arms
  // when the refreshed detail remounts the view.
  const [reinvesting, setReinvesting] = useState(false)

  async function send(next: 'up' | 'down') {
    if (!onFeedback || saving) return
    setSaving(true)
    try {
      await onFeedback(detail.id, { response_rating: next === 'up' ? 5 : 1 })
      setVote(next)   // record only on success → the buttons vanish
    } catch { /* leave the buttons so they can retry */ }
    finally { setSaving(false) }
  }

  async function sendNote() {
    if (!onFeedback || noteState !== 'idle' || !note.trim()) return
    setNoteState('saving')
    try {
      await onFeedback(detail.id, { user_feedback: note.trim() })
      setNoteState('sent')
    } catch { setNoteState('idle') }   // keep the text so they can retry
  }

  async function reinvestigate() {
    if (!onReinvestigate || reinvesting) return
    setReinvesting(true)
    try { await onReinvestigate(detail.id) }
    catch { setReinvesting(false) }    // failed to start → let them retry
  }

  return (
    <div className="nw-inc-fb">
      {onFeedback && investigated && (
        <div className="nw-inc-fb-row">
          {vote ? (
            <span className="nw-inc-fb-thanks">{vote === 'up' ? '👍' : '👎'} Thanks for the feedback</span>
          ) : (
            <>
              <span className="l">Was this helpful?</span>
              <div className="nw-inc-vote">
                <button type="button" className="vote" onClick={() => send('up')} disabled={saving} aria-label="Helpful">👍</button>
                <button type="button" className="vote" onClick={() => send('down')} disabled={saving} aria-label="Not helpful">👎</button>
              </div>
            </>
          )}
        </div>
      )}
      {onFeedback && (
        <div className="nw-inc-fb-row">
          <span className="l">How important is this to you?</span>
          <div className="nw-inc-imp" role="radiogroup" aria-label="Importance">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                className={'imp' + (importance === n ? ' on' : '')}
                onClick={() => setPriority(n)}
                disabled={impSaving}
                role="radio"
                aria-checked={importance === n}
                aria-label={`Importance ${n}`}
              >{n}</button>
            ))}
          </div>
        </div>
      )}
      {onFeedback && investigated && (
        noteState === 'sent' ? (
          <div className="nw-inc-fb-thanks">Feedback noted ✓</div>
        ) : (
          <div className="nw-inc-fb-note">
            <textarea
              rows={2}
              placeholder="Add feedback — what was right, wrong or missing?"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={noteState === 'saving'}
            />
            <button type="button" onClick={sendNote} disabled={!note.trim() || noteState === 'saving'}>
              {noteState === 'saving' ? 'Sending…' : 'Send'}
            </button>
          </div>
        )
      )}
      {onReinvestigate && (
        <div className="nw-inc-fb-actions">
          <button type="button" className="nw-inc-reinv" onClick={reinvestigate} disabled={reinvesting}>
            <span className={reinvesting ? 'nw-inc-spin' : undefined}>⟳</span>
            {' '}{reinvesting ? 'Investigating…' : 'Reinvestigate'}
          </button>
        </div>
      )}
    </div>
  )
}
